import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api, type LanguageOption, type VocabWord } from "../../api";
import { useProfile } from "../../layout/AppShell";
import { fadeUp, stagger } from "../../lib/motion";
import { cn } from "../../lib/format";

const BOT_URL = `https://t.me/${import.meta.env.VITE_BOT_USERNAME || "ilmli_bot"}`;
const GENDER_COLOR: Record<string, string> = {
  m: "#3A6EA5",
  f: "#B8472C",
  n: "#3F6A44",
};
const GENDER_LABEL: Record<string, string> = {
  m: "masculine",
  f: "feminine",
  n: "neuter",
};

/**
 * Build the lowercased set of definite articles for a language using the
 * catalog from /api/languages. Empty set means "this language doesn't
 * use articles" (e.g. ja, zh, ko) — rows just render without one.
 */
function articleSetFor(lang: LanguageOption | undefined): Set<string> {
  if (!lang?.gender_articles) return new Set();
  return new Set(Object.values(lang.gender_articles).map((a) => a.toLowerCase()));
}

type Outcome = "knew" | "half" | "forgot";

function todayBanner(): string {
  const d = new Date();
  const day = d.toLocaleDateString([], { weekday: "long" });
  const month = d.toLocaleDateString([], { month: "long" });
  return `${day} · ${month} ${d.getDate()}`;
}

function articleFor(
  word: VocabWord,
  articles: Set<string>,
  genderTable: Record<string, string>,
): { article: string | null; core: string } {
  const raw = (word.word || "").trim();
  const parts = raw.split(/\s+/);
  if (parts.length >= 2 && articles.has(parts[0].toLowerCase())) {
    return { article: parts[0].toLowerCase(), core: parts.slice(1).join(" ") };
  }
  if (word.gender && genderTable[word.gender]) {
    return { article: genderTable[word.gender], core: raw };
  }
  return { article: null, core: raw };
}

export default function Today() {
  const { profile } = useProfile();
  const [due, setDue] = useState<VocabWord[] | null>(null);
  const [catalog, setCatalog] = useState<LanguageOption[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [showMeaning, setShowMeaning] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api
      .getWords("due", profile.active_language)
      .then((r) => setDue(r.words))
      .catch((e) => setErr(e instanceof Error ? e.message : "load failed"));
  }, [profile.active_language]);

  useEffect(() => {
    api.getLanguages().then((r) => setCatalog(r.languages || [])).catch(() => {});
  }, []);

  // Per-language article rules looked up from /api/languages — no
  // German-specific hard-coding so French/Italian/Spanish render right.
  const langMeta = useMemo(
    () => catalog.find((l) => l.code === profile.active_language),
    [catalog, profile.active_language],
  );
  const articles = useMemo(() => articleSetFor(langMeta), [langMeta]);
  const genderTable = useMemo(
    () => (langMeta?.gender_articles ?? {}) as Record<string, string>,
    [langMeta],
  );

  const total = due?.length ?? 0;
  const current = due?.[idx];
  const upNext = useMemo(() => due?.slice(idx + 1, idx + 4) ?? [], [due, idx]);
  const finished = total > 0 && idx >= total;

  // Map the human-friendly button labels to FSRS rating ints. The
  // backend's /api/review_outcome wraps fsrs_wrapper.rate(), which then
  // updates stability/difficulty/next_due exactly like the in-chat
  // signal extractor does.
  const RATING_FOR: Record<Outcome, 1 | 2 | 3 | 4> = {
    forgot: 1,
    half: 2,
    knew: 3,
  };

  async function record(outcome: Outcome) {
    if (!current || posting) return;
    setPosting(true);
    setErr(null);
    try {
      await api.postReviewOutcome(current.word, RATING_FOR[outcome], profile.active_language);
      setShowMeaning(false);
      setIdx((i) => i + 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't record review");
    } finally {
      setPosting(false);
    }
  }

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      <motion.div variants={fadeUp}>
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
          {todayBanner()}
        </div>
        <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
          {total === 0 && due ? (
            <>All caught up.</>
          ) : (
            <>
              {total === 1 ? "One word is" : `${total || "—"} words are`}{" "}
              <span className="italic text-terracotta">due.</span>
            </>
          )}
        </h1>
        <p className="mt-2 max-w-xl font-serif italic text-ink-soft text-base">
          {total > 0
            ? "Five-minute review. Your tutor will weave them into today's chat anyway, but a quick pass makes them stick."
            : due
              ? "Nothing on the slate today. Tomorrow's batch will land when it's earned its keep."
              : "Reading today's queue…"}
        </p>
      </motion.div>

      {err && <div className="mt-6 text-terracotta text-sm">{err}</div>}

      {/* Card */}
      {current && !finished && (
        <motion.div
          variants={fadeUp}
          key={current.word}
          className="mt-10 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper-soft/50 p-6 md:p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-6 md:gap-10">
            <div>
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-terracotta">
                {idx + 1} of {total} · {bucketLabel(current.mastery)}
              </div>
              <ReviewWord
                word={current}
                articles={articles}
                genderTable={genderTable}
                capitalizeNouns={profile.active_language === "de"}
              />
              {current.gloss && (
                <div className="mt-4 font-serif italic text-[14px] text-ink-faint">
                  {showMeaning
                    ? current.gloss
                    : <span className="opacity-0 select-none">_</span>}
                </div>
              )}
            </div>

            <div className="md:border-l md:border-[rgba(42,36,26,0.15)] md:pl-7 flex flex-col">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
                How well did you know it?
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <ReviewButton variant="primary" onClick={() => record("knew")} disabled={posting}>
                  I knew it
                </ReviewButton>
                <ReviewButton onClick={() => record("half")} disabled={posting}>Half-knew</ReviewButton>
                <ReviewButton onClick={() => record("forgot")} disabled={posting}>Forgot</ReviewButton>
                <button
                  type="button"
                  onClick={() => setShowMeaning((s) => !s)}
                  className="self-start mt-2 py-2 font-mono text-[12px] text-ink-soft underline decoration-ink-faint/40 hover:decoration-terracotta hover:text-terracotta"
                >
                  {showMeaning ? "Hide meaning" : "Show meaning"}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Progress segments */}
      {due && total > 0 && (
        <motion.div variants={fadeUp} className="mt-7 flex items-center gap-2">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                i < idx ? "bg-terracotta" : "bg-[rgba(42,36,26,0.15)]",
                i === idx && "bg-terracotta",
              )}
            />
          ))}
        </motion.div>
      )}

      {/* Up next */}
      {upNext.length > 0 && (
        <motion.div variants={fadeUp} className="mt-5 flex items-baseline gap-3 flex-wrap">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-faint">
            up next:
          </span>
          {upNext.map((w, i) => {
            const { article, core } = articleFor(w, articles, genderTable);
            return (
              <span
                key={`${w.word}-${i}`}
                className="font-serif italic text-[14px] text-ink-soft"
              >
                {i > 0 && <span className="text-ink-faint mr-2">·</span>}
                {article && (
                  <span style={{ color: w.gender ? GENDER_COLOR[w.gender] : "var(--ink-faint)" }}>
                    {article}{" "}
                  </span>
                )}
                {core}
              </span>
            );
          })}
        </motion.div>
      )}

      {/* Finished */}
      {finished && (
        <motion.div
          variants={fadeUp}
          className="mt-10 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper-soft/50 p-8 text-center"
        >
          <h2 className="font-serif text-2xl">Done. Nice pass.</h2>
          <p className="mt-2 font-serif italic text-ink-soft max-w-md mx-auto">
            They'll show up again in chat over the next few days, weaving back in
            where they belong.
          </p>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-ink/80 px-4 py-2 text-[13px] hover:bg-ink hover:text-paper transition-colors"
          >
            Open Telegram <ArrowRight size={13} />
          </a>
        </motion.div>
      )}

      {/* Telegram alternative */}
      {due && total > 0 && (
        <motion.div
          variants={fadeUp}
          className="mt-10 rounded-md border-2 border-dashed border-[rgba(42,36,26,0.18)] px-6 py-5 flex items-center gap-4 flex-wrap"
        >
          <span className="font-serif italic text-ink-soft text-[14px]">
            Prefer this in Telegram?
          </span>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block py-2 font-mono text-[12px] text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
          >
            Open the review there →
          </a>
        </motion.div>
      )}
    </motion.div>
  );
}

function bucketLabel(mastery: number): string {
  if (mastery >= 4) return "mastered";
  if (mastery >= 2) return "comfortable";
  return "learning";
}

function ReviewWord({
  word,
  articles,
  genderTable,
  capitalizeNouns,
}: {
  word: VocabWord;
  articles: Set<string>;
  genderTable: Record<string, string>;
  capitalizeNouns: boolean;
}) {
  const { article, core } = articleFor(word, articles, genderTable);
  // Only German capitalises noun-stems mid-sentence — pass capitalize via prop
  // so French/Spanish/etc. don't accidentally Title-Case lowercase nouns.
  const display = capitalizeNouns && word.gender && word.pos === "noun" && core.length > 0
    ? core[0].toUpperCase() + core.slice(1)
    : core;
  return (
    <>
      <div className="mt-3 flex items-baseline gap-3 flex-wrap">
        {article && (
          <span
            className="font-serif italic text-2xl"
            style={{ color: word.gender ? GENDER_COLOR[word.gender] : "var(--ink-faint)" }}
          >
            {article}
          </span>
        )}
        <span className="font-serif text-5xl md:text-6xl leading-none italic">
          {display}
        </span>
      </div>
      <div className="mt-2 font-serif italic text-ink-soft text-[14px]">
        {[
          word.pos || null,
          word.gender ? GENDER_LABEL[word.gender] : null,
          word.level || null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </>
  );
}

function ReviewButton({
  variant,
  onClick,
  disabled,
  children,
}: {
  variant?: "primary";
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-md px-4 py-2 text-[14px] text-left transition-colors disabled:opacity-60",
        variant === "primary"
          ? "bg-terracotta text-paper hover:bg-terracotta-soft"
          : "border border-ink/80 text-ink hover:bg-ink hover:text-paper",
      )}
    >
      {children}
    </button>
  );
}
